import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  alpha,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Button,
  Chip,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
  ListItemText,
  ListItemIcon,
  MenuItem,
  Menu
} from '@mui/material';
import {
  ThumbUp,
  ThumbDown,
  ChatBubble,
  Share,
  PostAdd,
  Groups,
  MoreVert,
  VolumeUp,
  VolumeOff,
  Edit, 
  Flag, 
 Delete,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { postApi } from '../../../services/postApi';
import toast from 'react-hot-toast';
import EditPostModal from '../../community/EditPostModal';
import { PostListSkeleton } from '../../skeletons';

interface PostsTabProps {
  colors: any;
  glassStyle: any;
  isDark: boolean;
  user: any;
  navigate: any;
  goldColor: string;
  deepGreen: string;
}

interface UserPost {
  isAuthor: boolean;
  id: string;
  title: string;
  content: string;
  type: 'text' | 'image' | 'video';
  mediaUrl?: string;
  author: {
    id: string;
    username: string;
    profilePicture?: string;
  };
  community?: {
    id: string;
    name: string;
    slug: string;
    profilePicture?: string;
  };
  tags: string[];
  upvotes: number;
  downvotes: number;
  commentsCount: number;
  createdAt: string;
  userVote: 'up' | 'down' | null;
  score?: number;
}

const PostsTab: React.FC<PostsTabProps> = ({
  colors,
  glassStyle,
  isDark,
  user,
  navigate,
  goldColor,
  deepGreen,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const videoRefs = useRef<{[key: string]: HTMLVideoElement | null}>({});
  
  const [userPosts, setUserPosts] = useState<UserPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewAllMode, setViewAllMode] = useState(false);
  const [videoMuted, setVideoMuted] = useState<{[key: string]: boolean}>({});

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPost, setSelectedPost] = useState<UserPost | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deletingPostId, setDeletingPostId] = useState<string | null>(null);

  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, post: UserPost) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedPost(post);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPost(null);
  };

  // Handle edit post
  const handleEditPost = () => {
    if (selectedPost) {
      setEditModalOpen(true);
      handleMenuClose();
    }
  };

  // Handle delete post
  const handleDeletePost = async () => {
    if (!selectedPost) return;
    
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      handleMenuClose();
      return;
    }

    try {
      setDeletingPostId(selectedPost.id);
      const response = await postApi.deletePost(selectedPost.id);
      
      if (response.success) {
        toast.success('Post deleted successfully');
        // Remove post from state
        setUserPosts(prev => prev.filter(post => post.id !== selectedPost.id));
      } else {
        toast.error(response.error || 'Failed to delete post');
      }
    } catch (error: any) {
      console.error('Delete post error:', error);
      toast.error(error.message || 'Failed to delete post');
    } finally {
      setDeletingPostId(null);
      handleMenuClose();
    }
  };

    const handleSharePost = () => {
    if (selectedPost) {
      navigator.clipboard.writeText(`${window.location.origin}/post/${selectedPost.id}`)
        .then(() => {
          toast.success('Post link copied to clipboard!');
        })
        .catch((err) => {
          console.error('Failed to copy link: ', err);
          toast.error('Failed to copy link');
        });
      handleMenuClose();
    }
  };

  const handleReportPost = () => {
    // Implement report functionality
    toast.success('Post reported successfully');
    handleMenuClose();
  };

  // Check if current user is the author of the post
  const isPostAuthor = (post: UserPost) => {
  return post.isAuthor === true; 
};

  // Handle edit success
  const handleEditSuccess = (updatedPost: any) => {
  // Update the post in the state
  setUserPosts(prev => prev.map(post => 
    post.id === updatedPost.id 
      ? { 
          ...post, 
          title: updatedPost.title,
          content: updatedPost.content,
          tags: updatedPost.tags,
          updatedAt: updatedPost.updatedAt
        }
      : post
  ));
  setEditModalOpen(false);
  toast.success('Post updated successfully');
  
  // Optionally refresh to get latest data
  fetchUserPosts();
};

  const glassEffect = {
    background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    border: isDark 
      ? '1px solid rgba(196, 182, 111, 0.2)' 
      : '1px solid rgba(5, 24, 20, 0.1)',
    boxShadow: isDark 
      ? '0 20px 60px rgba(0,0,0,0.7)' 
      : '0 20px 60px rgba(5, 24, 20, 0.1)',
  };

  // Fetch user's posts
  const fetchUserPosts = async () => {
  if (!user) {
    console.log('No user found, skipping post fetch');
    toast.error('Please login to view your posts');
    return;
  }
  
  try {
    setLoading(true);
    console.log('Fetching user posts...');
    
    // Use the authenticated endpoint which automatically handles vote status
    const response = await postApi.getPosts({
      author: user.id || user._id || user.userId,
      sortBy: 'new',
      page: 1,
      limit: viewAllMode ? 50 : 6
    });
    
    console.log('User posts response:', response);
    
    if (response.success && response.posts) {
      // Sort posts by date (newest first)
      const sortedPosts = response.posts
        .map(post => ({
          ...post,
          score: (post.upvotes || 0) - (post.downvotes || 0),
          // Ensure isAuthor is set for posts fetched for the current user
          isAuthor: true // Since we're fetching posts by author filter, all posts should be authored by the user
        }))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      console.log(`Loaded ${sortedPosts.length} user posts`);
      setUserPosts(sortedPosts);
    } else {
      console.log('Failed to fetch user posts:', response.error);
      toast.error(response.error || 'Failed to load your posts');
    }
  } catch (error: any) {
    console.error('Failed to fetch user posts:', error);
    toast.error('Failed to load your posts');
    setUserPosts([]);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    if (user) {
      fetchUserPosts();
    }
  }, [user, viewAllMode]);

  // Video mute toggle
  const toggleVideoMute = (postId: string) => {
    const video = videoRefs.current[postId];
    if (video) {
      video.muted = !video.muted;
      setVideoMuted(prev => ({
        ...prev,
        [postId]: video.muted
      }));
    }
  };

  const handleVideoRef = (postId: string, el: HTMLVideoElement | null) => {
    videoRefs.current[postId] = el;
    if (el) {
      el.loop = true;
      el.autoplay = true;
      el.playsInline = true;
      el.muted = videoMuted[postId] || true;
    }
  };

  const handleCreatePost = () => {
    navigate('/community');
  };

  const handlePostClick = (postId: string) => {
    navigate(`/post/${postId}`);
  };

  const handleVote = async (postId: string, vote: 'up' | 'down' | 'remove') => {
  if (!user) {
    toast.error('Please login to vote');
    return;
  }

  try {
    // Send the vote to API
    const response = await postApi.votePost(postId, vote);
    
    if (response.success) {
      // Update local state
      setUserPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            upvotes: response.post.upvotes,
            downvotes: response.post.downvotes,
            score: response.post.score,
            userVote: response.post.userVote
          };
        }
        return post;
      }));
      
      toast.success('Vote updated');
    }
  } catch (error) {
    console.error('Vote error:', error);
    toast.error('Failed to vote on post');
    // Refresh posts
    fetchUserPosts();
  }
};

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <PostListSkeleton count={3} />;
  }

  const displayedPosts = viewAllMode ? userPosts : userPosts.slice(0, 6);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        mb: 4,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color={colors.textPrimary} sx={{ mb: 0.5 }}>
            My Posts
          </Typography>
          <Typography variant="body2" color={colors.textSecondary}>
            {userPosts.length} total posts • {userPosts.reduce((acc, post) => acc + (post.upvotes || 0), 0)} total upvotes
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {userPosts.length > 6 && (
            <Button
              variant="outlined"
              onClick={() => setViewAllMode(!viewAllMode)}
              sx={{
                color: goldColor,
                borderColor: alpha(goldColor, 0.3),
                fontWeight: 600,
                fontSize: '0.85rem',
                py: 0.8,
                px: 2,
                borderRadius: '12px',
                '&:hover': {
                  borderColor: goldColor,
                  bgcolor: alpha(goldColor, 0.1)
                }
              }}
            >
              {viewAllMode ? 'Show Less' : `View All (${userPosts.length})`}
            </Button>
          )}
          
          <Button
            variant="contained"
            startIcon={<PostAdd />}
            onClick={handleCreatePost}
            sx={{
              bgcolor: goldColor,
              color: deepGreen,
              fontWeight: 700,
              textTransform: 'none',
              fontSize: '0.85rem',
              py: 0.8,
              px: 2,
              borderRadius: '12px',
              boxShadow: `0 4px 20px ${alpha(goldColor, 0.3)}`,
              '&:hover': {
                bgcolor: alpha(goldColor, 0.9),
                boxShadow: `0 8px 30px ${alpha(goldColor, 0.4)}`,
              }
            }}
          >
            New Post
          </Button>
        </Box>
      </Box>

      {/* Posts List */}
      {userPosts.length === 0 ? (
        <Paper elevation={0} sx={{ 
          bgcolor: colors.cardBg, 
          borderRadius: '24px', 
          p: 6, 
          textAlign: 'center',
          ...glassStyle
        }}>
          <PostAdd sx={{ fontSize: 60, color: goldColor, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color={colors.textPrimary} sx={{ mb: 1.5, fontWeight: 700 }}>
            No Posts Yet
          </Typography>
          <Typography variant="body2" color={colors.textSecondary} sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
            You haven't created any posts yet. Share your thoughts, insights, or questions with the community!
          </Typography>
          <Button
            variant="contained"
            startIcon={<PostAdd />}
            onClick={handleCreatePost}
            sx={{
              bgcolor: goldColor,
              color: deepGreen,
              fontWeight: 700,
              textTransform: 'none',
              py: 1.2,
              px: 3,
              borderRadius: '12px',
              boxShadow: `0 4px 20px ${alpha(goldColor, 0.3)}`,
              '&:hover': {
                bgcolor: alpha(goldColor, 0.9),
                boxShadow: `0 8px 30px ${alpha(goldColor, 0.4)}`,
              }
            }}
          >
            Create Your First Post
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {displayedPosts.map((post) => (
            <React.Fragment key={post.id}>
              {isMobile && (
                <Divider 
                  sx={{ 
                    mx: 0,
                    my: 0,
                    borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.2)',
                    borderBottomWidth: '1px'
                  }} 
                />
              )}
              <Box
                sx={{
                  mb: isMobile ? 0 : 3,
                  borderRadius: isMobile ? 0 : '20px',
                  overflow: 'hidden',
                  ...(isMobile ? {} : glassEffect),
                  width: '100%',
                  position: 'relative',
                  cursor: 'pointer',
                  '&:hover': !isMobile ? {
                    transform: 'translateY(-2px)',
                    boxShadow: isDark 
                      ? '0 20px 60px rgba(0,0,0,0.9)' 
                      : '0 20px 60px rgba(5, 24, 20, 0.2)',
                    transition: 'all 0.2s ease-in-out'
                  } : {}
                }}
              >
                {/* Clickable Content Area */}
                <Box 
                  onClick={() => handlePostClick(post.id)}
                  sx={{ 
                    p: isMobile ? 2 : 3,
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  {/* Header section with avatar and user info */}
                  {/* Header section with avatar and user info */}
<Box sx={{ 
  display: 'flex', 
  alignItems: 'flex-start', 
  gap: isMobile ? 1.5 : 3, 
  mb: isMobile ? 1.5 : 3 
}}>
  <Avatar
    src={post.author?.profilePicture}
    sx={{
      width: isMobile ? 40 : 52,
      height: isMobile ? 40 : 52,
      border: `2px solid ${alpha(goldColor, 0.3)}`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}
  >
    {post.author?.username?.charAt(0)?.toUpperCase() || 'U'}
  </Avatar>
  <Box sx={{ flex: 1 }}>
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'space-between', // Changed to space-between
      gap: isMobile ? 1 : 1.5,
      flexWrap: 'wrap' 
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 1.5, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ 
          fontWeight: 800, 
          color: isDark ? '#fff' : deepGreen, 
          fontSize: isMobile ? '0.95rem' : '1.1rem' 
        }}>
          {post.author?.username || 'Unknown'}
        </Typography>
        {post.community && (
          <>
            <Typography variant="caption" sx={{ 
              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', 
              fontSize: isMobile ? '0.7rem' : '0.8rem' 
            }}>in</Typography>
            <Box
              component="span"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/community/${post.community?.slug}`);
              }}
              sx={{
                color: goldColor,
                fontWeight: 800,
                fontSize: isMobile ? '0.75rem' : '0.9rem',
                cursor: 'pointer',
                background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)',
                px: isMobile ? 1 : 1.5,
                py: isMobile ? 0.25 : 0.5,
                borderRadius: '10px',
                transition: 'all 0.2s',
                '&:hover': {
                  background: isDark ? 'rgba(196, 182, 111, 0.2)' : 'rgba(196, 182, 111, 0.1)',
                  textDecoration: 'none'
                }
              }}
            >
              wfh/{post.community?.slug}
            </Box>
          </>
        )}
      </Box>
      
      {/* Add MoreVert icon button - Only show on desktop */}
      {!isMobile && (
        <IconButton
          onClick={(e) => handleMenuOpen(e, post)}
          sx={{
            color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
            padding: '8px',
            '&:hover': {
              color: goldColor,
              backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
            }
          }}
        >
          <MoreVert fontSize="medium" />
        </IconButton>
      )}
    </Box>
    <Typography variant="caption" sx={{ 
      color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
      fontSize: isMobile ? '0.7rem' : '0.8rem'
    }}>
      {formatDate(post.createdAt)}
    </Typography>
  </Box>
</Box>

                  {/* Post title */}
                  <Typography variant="h5" sx={{
                    fontWeight: 900,
                    mb: isMobile ? 1.5 : 2.5,
                    lineHeight: isMobile ? 1.3 : 1.4,
                    color: isDark ? '#fff' : deepGreen,
                    fontSize: isMobile ? '1.1rem' : '1.4rem',
                    wordBreak: 'break-word'
                  }}>
                    {post.title}
                  </Typography>

                  {/* Media section */}
                  {(post.type === 'image' || post.type === 'video') && post.mediaUrl && (
                    <Box sx={{
                      mb: isMobile ? 2 : 3,
                      borderRadius: '10px',
                      overflow: 'hidden',
                      position: 'relative',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                      maxHeight: '400px'
                    }}>
                      {post.type === 'image' ? (
                        <CardMedia
                          component="img"
                          image={post.mediaUrl}
                          alt="Post"
                          sx={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: '400px',
                            objectFit: 'contain',
                            backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)'
                          }}
                        />
                      ) : (
                        <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                          <video
                            ref={(el) => handleVideoRef(post.id, el)}
                            src={post.mediaUrl}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '10px'
                            }}
                            loop
                            autoPlay
                            playsInline
                            muted={videoMuted[post.id] !== false}
                          />
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleVideoMute(post.id);
                            }}
                            sx={{
                              position: 'absolute',
                              bottom: 10,
                              right: 10,
                              bgcolor: 'rgba(0,0,0,0.6)',
                              color: '#fff',
                              width: 32,
                              height: 32,
                              fontSize: '0.9rem',
                              '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.8)'
                              }
                            }}
                          >
                            {videoMuted[post.id] === false ? <VolumeUp /> : <VolumeOff />}
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Post content */}
                  {post.content && (
                    <Typography variant="body1" sx={{
                      mb: isMobile ? 2 : 3,
                      color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                      lineHeight: isMobile ? 1.6 : 1.7,
                      fontSize: isMobile ? '0.95rem' : '1.05rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {post.content.length > (isMobile ? 150 : 300) ? `${post.content.substring(0, isMobile ? 150 : 300)}...` : post.content}
                      {post.content.length > (isMobile ? 150 : 300) && (
                        <Button
                          size="small"
                          sx={{
                            color: goldColor,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: isMobile ? '0.8rem' : '0.9rem',
                            ml: 0.5,
                            minWidth: 'auto',
                            p: 0.5
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/post/${post.id}`);
                          }}
                        >
                          Read more
                        </Button>
                      )}
                    </Typography>
                  )}

                  {/* Tags section */}
                  {post.tags && post.tags.length > 0 && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: isMobile ? 0.5 : 1, 
                      mb: isMobile ? 2 : 3 
                    }}>
                      {post.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={`#${tag}`}
                          size="small"
                          sx={{
                            bgcolor: alpha(goldColor, 0.1),
                            color: goldColor,
                            fontWeight: 600,
                            borderRadius: '10px',
                            fontSize: isMobile ? '0.7rem' : '0.85rem',
                            height: isMobile ? 24 : 32,
                            '&:hover': {
                              bgcolor: alpha(goldColor, 0.2)
                            }
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Action buttons - SEPARATE FROM CLICKABLE AREA */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pt: 2,
                  borderTop: `1px solid ${alpha(goldColor, 0.1)}`,
                  px: isMobile ? 2 : 3,
                  pb: isMobile ? 2 : 3
                }}>
                  {!isMobile ? (
                    // DESKTOP ACTION BUTTONS
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2, 
                      maxWidth: '100%', 
                      overflowX: 'auto',
                      flexWrap: 'nowrap'
                    }}>
                      <Button
                        startIcon={<ThumbUp />}
                        onClick={() => handleVote(post.id, post.userVote === 'up' ? 'remove' : 'up')}
                        sx={{
                          minWidth: 'auto',
                          color: post.userVote === 'up' ? goldColor : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                          fontWeight: 700,
                          borderRadius: '20px',
                          px: 2,
                          py: 0.5,
                          bgcolor: post.userVote === 'up' ? alpha(goldColor, 0.1) : 'transparent',
                          ...(post.userVote === 'up' ? {} : glassEffect),
                          fontSize: '0.9rem',
                          '&:hover': {
                            bgcolor: post.userVote === 'up' ? alpha(goldColor, 0.2) : alpha(goldColor, 0.05)
                          }
                        }}
                      >
                        {post.upvotes}
                      </Button>

                      <Button
                        startIcon={<ThumbDown />}
                        onClick={() => handleVote(post.id, post.userVote === 'down' ? 'remove' : 'down')}
                        sx={{
                          minWidth: 'auto',
                          color: post.userVote === 'down' ? goldColor : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                          fontWeight: 700,
                          borderRadius: '20px',
                          px: 2,
                          py: 0.5,
                          bgcolor: post.userVote === 'down' ? alpha(goldColor, 0.1) : 'transparent',
                          ...(post.userVote === 'down' ? {} : glassEffect),
                          fontSize: '0.9rem',
                          '&:hover': {
                            bgcolor: post.userVote === 'down' ? alpha(goldColor, 0.2) : alpha(goldColor, 0.05)
                          }
                        }}
                      >
                        {post.downvotes}
                      </Button>

                      <Button
                        startIcon={<ChatBubble />}
                        onClick={() => navigate(`/post/${post.id}`)}
                        sx={{
                          minWidth: 'auto',
                          color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                          fontWeight: 700,
                          borderRadius: '20px',
                          px: 2,
                          py: 0.5,
                          ...glassEffect,
                          fontSize: '0.9rem',
                          '&:hover': {
                            bgcolor: alpha(goldColor, 0.05)
                          }
                        }}
                      >
                        {post.commentsCount}
                      </Button>

                      <Button
                        startIcon={<Share />}
                        size="small"
                        sx={{
                          color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                          fontWeight: 600,
                          borderRadius: '20px',
                          px: 2,
                          py: 0.5,
                          fontSize: '0.85rem',
                          ...glassEffect,
                          '&:hover': {
                            bgcolor: alpha(goldColor, 0.05)
                          }
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/post/${post.id}`);
                          toast.success('Link copied to clipboard!');
                        }}
                      >
                        Share
                      </Button>
                    </Box>
                  ) : (
                    // MOBILE ACTION BUTTONS
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      width: '100%',
                      justifyContent: 'space-between'
                    }}>
                      <Button
                        startIcon={<ThumbUp sx={{ fontSize: '1.2rem' }} />}
                        onClick={() => handleVote(post.id, post.userVote === 'up' ? 'remove' : 'up')}
                        sx={{
                          minWidth: 'auto',
                          color: post.userVote === 'up' ? goldColor : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                          fontWeight: 700,
                          borderRadius: '20px',
                          px: 1.2,
                          py: 0.6,
                          bgcolor: post.userVote === 'up' ? alpha(goldColor, 0.1) : 'transparent',
                          fontSize: '0.85rem',
                          '&:hover': {
                            bgcolor: post.userVote === 'up' ? alpha(goldColor, 0.2) : alpha(goldColor, 0.05)
                          },
                          flex: 1,
                          justifyContent: 'center',
                          '& .MuiButton-startIcon': {
                            marginRight: 0.6
                          }
                        }}
                      >
                        {post.upvotes}
                      </Button>

                      <Button
                        startIcon={<ThumbDown sx={{ fontSize: '1.2rem' }} />}
                        onClick={() => handleVote(post.id, post.userVote === 'down' ? 'remove' : 'down')}
                        sx={{
                          minWidth: 'auto',
                          color: post.userVote === 'down' ? goldColor : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                          fontWeight: 700,
                          borderRadius: '20px',
                          px: 1.2,
                          py: 0.6,
                          bgcolor: post.userVote === 'down' ? alpha(goldColor, 0.1) : 'transparent',
                          fontSize: '0.85rem',
                          '&:hover': {
                            bgcolor: post.userVote === 'down' ? alpha(goldColor, 0.2) : alpha(goldColor, 0.05)
                          },
                          flex: 1,
                          justifyContent: 'center',
                          '& .MuiButton-startIcon': {
                            marginRight: 0.6
                          }
                        }}
                      >
                        {post.downvotes}
                      </Button>

                      <Button
                        startIcon={<ChatBubble sx={{ fontSize: '1.2rem' }} />}
                        onClick={() => navigate(`/post/${post.id}`)}
                        sx={{
                          minWidth: 'auto',
                          color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                          fontWeight: 700,
                          borderRadius: '20px',
                          px: 1.2,
                          py: 0.6,
                          fontSize: '0.85rem',
                          '&:hover': {
                            bgcolor: alpha(goldColor, 0.05)
                          },
                          flex: 1,
                          justifyContent: 'center',
                          '& .MuiButton-startIcon': {
                            marginRight: 0.6
                          }
                        }}
                      >
                        {post.commentsCount}
                      </Button>

                      <Button
                        startIcon={<Share sx={{ fontSize: '1.2rem' }} />}
                        size="small"
                        sx={{
                          minWidth: 'auto',
                          color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                          fontWeight: 600,
                          borderRadius: '20px',
                          px: 1.2,
                          py: 0.6,
                          fontSize: '0.85rem',
                          '&:hover': {
                            bgcolor: alpha(goldColor, 0.05)
                          },
                          flex: 1,
                          justifyContent: 'center',
                          '& .MuiButton-startIcon': {
                            marginRight: 0.6
                          }
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/post/${post.id}`);
                          toast.success('Link copied to clipboard!');
                        }}
                      >
                        Share
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </React.Fragment>
          ))}
        </Box>
      )}

      {/* View All Toggle */}
      {userPosts.length > 6 && !viewAllMode && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={() => setViewAllMode(true)}
            sx={{
              color: goldColor,
              borderColor: alpha(goldColor, 0.3),
              fontWeight: 600,
              fontSize: '0.9rem',
              py: 1,
              px: 3,
              borderRadius: '12px',
              '&:hover': {
                borderColor: goldColor,
                bgcolor: alpha(goldColor, 0.1)
              }
            }}
          >
            Load More Posts ({userPosts.length - 6} more)
          </Button>
        </Box>
      )}
            {/* Menu for post options */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: isDark 
              ? '1px solid rgba(196, 182, 111, 0.2)' 
              : '1px solid rgba(5, 24, 20, 0.1)',
            boxShadow: isDark 
              ? '0 20px 60px rgba(0,0,0,0.7)' 
              : '0 20px 60px rgba(5, 24, 20, 0.1)',
            borderRadius: '12px',
            mt: 1,
            minWidth: '180px'
          }
        }}
      >
        {/* Share option - visible to all */}
        <MenuItem onClick={handleSharePost}>
          <ListItemIcon>
            <Share fontSize="small" sx={{ color: goldColor }} />
          </ListItemIcon>
          <ListItemText sx={{ color: isDark ? '#fff' : deepGreen }}>Share Post</ListItemText>
        </MenuItem>

        {/* Edit option - only visible to post author */}
        {selectedPost && isPostAuthor(selectedPost) && (
          <>
            <Divider sx={{ borderColor: alpha(goldColor, 0.1) }} />
            <MenuItem onClick={handleEditPost}>
              <ListItemIcon>
                <Edit fontSize="small" sx={{ color: goldColor }} />
              </ListItemIcon>
              <ListItemText sx={{ color: isDark ? '#fff' : deepGreen }}>Edit Post</ListItemText>
            </MenuItem>
          </>
        )}

        {/* Delete option - only visible to post author */}
        {selectedPost && isPostAuthor(selectedPost) && (
          <MenuItem onClick={handleDeletePost} sx={{ color: '#ff4444' }}>
            <ListItemIcon>
              <Delete fontSize="small" sx={{ color: '#ff4444' }} />
            </ListItemIcon>
            <ListItemText>Delete Post</ListItemText>
          </MenuItem>
        )}

        <Divider sx={{ borderColor: alpha(goldColor, 0.1) }} />
        
        {/* Report option - visible to all */}
        <MenuItem onClick={handleReportPost} sx={{ color: '#ff4444' }}>
          <ListItemIcon>
            <Flag fontSize="small" sx={{ color: '#ff4444' }} />
          </ListItemIcon>
          <ListItemText>Report Post</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit Post Modal */}
      {selectedPost && (
        <EditPostModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSuccess={handleEditSuccess}
          post={selectedPost}
        />
      )}
    </Box>
  );
};

export default PostsTab;